import type { AnalyticsFlow } from 'pi-kiosk-shared';
import { ANALYTICS_UNIVERSAL_EVENTS } from 'pi-kiosk-shared/analyticsEvents';
import type { CustomerEmitEventInput } from './customerAnalyticsClient.js';
import { ApiError } from '../api/ApiClient.js';

export type CustomerErrorShownTrack = (
  input: CustomerEmitEventInput,
) => void | Promise<void>;

export interface EmitCustomerErrorShownInput {
  readonly track: CustomerErrorShownTrack;
  readonly flow?: AnalyticsFlow | null;
  readonly screenName?: string;
  readonly failureReason?: string | null;
  readonly error?: unknown;
  readonly operation?: string;
  readonly message?: string;
  readonly screen?: string;
  readonly recovery_class?: string;
  readonly recovery_ms_id?: string;
  readonly feature?: string;
  readonly boundary_layer?: string;
  readonly correlation_id?: string;
}

function resolveErrorCode(error: unknown): string | undefined {
  if (error instanceof ApiError && error.code !== undefined) {
    return error.code;
  }
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string') {
      return code;
    }
  }
  return undefined;
}

/** Plan §5.5 P1 — `error_shown` when customer-visible error UI is surfaced. */
export function emitCustomerErrorShown(
  input: EmitCustomerErrorShownInput,
): void | Promise<void> {
  const errorCode = resolveErrorCode(input.error);
  const message =
    input.message ??
    (input.error instanceof Error && input.error.message.trim().length > 0
      ? input.error.message
      : undefined);

  return input.track({
    eventName: ANALYTICS_UNIVERSAL_EVENTS.ERROR_SHOWN,
    flow: input.flow ?? null,
    screenName: input.screenName,
    failureReason: input.failureReason ?? errorCode ?? input.operation ?? null,
    metadata: {
      ...(input.screen !== undefined ? { screen: input.screen } : {}),
      ...(errorCode !== undefined ? { error_code: errorCode } : {}),
      ...(message !== undefined ? { error_message: message, message } : {}),
      ...(input.operation !== undefined ? { operation: input.operation } : {}),
      ...(input.recovery_class !== undefined ? { recovery_class: input.recovery_class } : {}),
      ...(input.recovery_ms_id !== undefined ? { recovery_ms_id: input.recovery_ms_id } : {}),
      ...(input.feature !== undefined ? { feature: input.feature } : {}),
      ...(input.boundary_layer !== undefined ? { boundary_layer: input.boundary_layer } : {}),
      ...(input.correlation_id !== undefined ? { correlation_id: input.correlation_id } : {}),
    },
  });
}
